import React, { useState } from 'react'
import './projects.css'

const Projects = () => {
  const [selectedProject, setSelectedProject] = useState(null)

  const projects = [
    {
      id: 1,
      title: 'POS System Backend',
      subtitle: 'Enterprise Point-of-Sale Platform',
      technologies: ['Java', 'Spring Boot', 'Spring Security', 'PostgreSQL', 'Docker', 'Flyway', 'GitHub Actions'],
      highlights: [
        'RESTful API supporting 6+ domain entities with complex business logic',
        'JWT authentication with RBAC for 4+ user roles',
        'PostgreSQL migration with versioned Flyway migrations',
        'Automated CI/CD with Docker & GitHub Actions'
      ],
      fullAchievements: [
        'Built and deployed a RESTful Point-of-Sale backend supporting stores, branches, products, inventory, customers, orders, refunds, and role-based user management.',
        'Implemented JWT-based authentication and role-based access control across multiple roles, including store administrators, branch managers, cashiers, and customers.',
        'Added order and shift-reporting workflows to track sales, order counts, payment-type summaries, refunds, recent orders, and top-selling products.',
        'Migrated the application from MySQL to PostgreSQL for cloud deployment and introduced Flyway-based versioned database migrations, replacing environment-dependent schema creation with production-oriented schema validation.',
        'Containerized the application with Docker and implemented a GitHub Actions release pipeline that builds versioned Docker images, pushes them to Docker Hub, triggers deployment on Render, and tags release commits.',
        'Added Swagger/OpenAPI documentation and Spring Boot Actuator to improve API discoverability and operational monitoring.'
      ],
      links: {
        code: 'https://github.com/TejasPatne/POS-System',
        demo: 'https://pos-system-ydoj.onrender.com/'
      }
    },
    {
      id: 2,
      title: 'Job Portal Microservices Platform',
      subtitle: 'Scalable Service Architecture',
      technologies: ['Spring Boot', 'Spring Cloud', 'PostgreSQL', 'RabbitMQ', 'Docker Compose', 'Eureka', 'Zipkin'],
      highlights: [
        '3 independently deployable microservices',
        'Service discovery with Eureka & Cloud Gateway',
        'Hybrid sync/async communication patterns',
        'Distributed tracing & rate limiting'
      ],
      fullAchievements: [
        'Built a job portal using 3 independently deployable Spring Boot services for Job, Company, and Review management, with separate service infrastructure for API Gateway, configuration, and service discovery.',
        'Implemented service discovery with Eureka, centralized configuration with Spring Cloud Config, and request routing through Spring Cloud Gateway.',
        'Integrated synchronous service-to-service communication using OpenFeign and asynchronous communication using RabbitMQ for review-driven company rating updates.',
        'Applied Resilience4j rate limiting and implemented distributed tracing with Micrometer and Zipkin to explore resilience and observability patterns in a microservices architecture.',
        'Containerized supporting infrastructure with Docker Compose for reproducible local development and orchestration.'
      ],
      links: {
        code: 'https://github.com/TejasPatne/FirstJobApplication'
      }
    }
  ]

  const openModal = (id) => {
    setSelectedProject(projects.find(p => p.id === id))
    document.body.style.overflow = 'hidden'
  }

  const closeModal = () => {
    setSelectedProject(null)
    document.body.style.overflow = 'unset'
  }

  return (
    <>
      <section id="projects" className='card-container'>
        <h5>Highlighted Work</h5>
        <h2>Projects</h2>

        <div className="container projects__container">
          <div className="projects__grid">
            {projects.map((project) => (
              <div key={project.id} className="projects__card">
                <div className="projects__card-header">
                  <div className="projects__title-group">
                    <h3>{project.title}</h3>
                    <p className="projects__subtitle">{project.subtitle}</p>
                  </div>
                  <button 
                    className="projects__expand-btn"
                    onClick={() => openModal(project.id)}
                    aria-label="View project details"
                    title="View details"
                  >
                    ⋯
                  </button>
                </div>

                <div className="projects__tech-stack">
                  {project.technologies.map((tech, idx) => (
                    <span key={idx} className="tech-badge">{tech}</span>
                  ))}
                </div>

                <div className="projects__highlights">
                  <ul>
                    {project.highlights.map((highlight, idx) => (
                      <li key={idx}>{highlight}</li>
                    ))}
                  </ul>
                </div>

                <div className="projects__footer">
                  <div className="projects__links">
                    {project.links.code && (
                      <a target="_blank" href={project.links.code} className="project-link code-link" title="View Source Code">
                        <span className="link-icon">{'</>'}</span> Code
                      </a>
                    )}
                    {project.links.demo && (
                      <a target="_blank" href={project.links.demo} className="project-link demo-link" title="View Live Demo">
                        <span className="link-icon">→</span> Demo
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <p className="projects__scroll-hint" aria-hidden="true">Swipe to explore projects <span>→</span></p>
      </section>

      {selectedProject && (
        <div className="projects__modal-overlay" onClick={closeModal}>
          <div className="projects__modal" onClick={(e) => e.stopPropagation()}>
            <button className="projects__modal-close" onClick={closeModal} aria-label="Close modal">×</button>
            
            <div className="projects__modal-header">
              <h2>{selectedProject.title}</h2>
              <p className="projects__modal-subtitle">{selectedProject.subtitle}</p>
            </div>

            <div className="projects__modal-tech">
              {selectedProject.technologies.map((tech, idx) => (
                <span key={idx} className="tech-badge">{tech}</span>
              ))}
            </div>

            <div className="projects__modal-achievements">
              <h4>Project Details</h4>
              <ul>
                {selectedProject.fullAchievements.map((achievement, idx) => (
                  <li key={idx}>{achievement}</li>
                ))}
              </ul>
            </div>

            <div className="projects__modal-footer">
              <div className="projects__modal-links">
                {selectedProject.links.code && (
                  <a href={selectedProject.links.code} className="project-link code-link" title="View Source Code" target="_blank" rel="noopener noreferrer">
                    <span className="link-icon">{'</>'}</span> Code
                  </a>
                )}
                {selectedProject.links.demo && (
                  <a href={selectedProject.links.demo} className="project-link demo-link" title="View Live Demo" target="_blank" rel="noopener noreferrer">
                    <span className="link-icon">→</span> Demo
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default Projects
